// import components
import { Box, Typography, styled } from '@mui/material';

// import constants
import { ORANGE, WHITE } from 'constants/colors';

/**
 * The signal markers' own geometry: a 14px icon, inset from the card's top-right corner. Named here
 * because two things depend on it — the markers themselves, and the room the title row has to hold free
 * so the time never ends up underneath them.
 */
const SIGNAL_ICON_SIZE = 14;
const SIGNAL_INSET = 4;
/** What the markers occupy of the card's right edge, plus a gap so text does not touch the icon. */
const SIGNAL_COLUMN_WIDTH = SIGNAL_INSET + SIGNAL_ICON_SIZE + SIGNAL_INSET;

/**
 * One stray appointment, styled to read like the cards in afsprakenplanning: a coloured accent, the
 * activity title, and — when there are any — the other participants, with a recurrence marker pinned in
 * the top-right corner. A card with no participants is just its title line. Right-clicking opens the
 * actions menu. A non-editable appointment is greyed out and set in italic, the same read-only treatment
 * afsprakenplanning gives an appointment that cannot be edited. While its right-click menu is open the
 * card is marked with `data-selected` and gets a black ring that wraps the whole card — the accent line
 * included — so it is clear which appointment is acted on, the same way afsprakenplanning outlines the
 * selected appointment in black.
 */
export const Card = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isReadOnly' && prop !== 'accentColor'
})<{ isReadOnly?: boolean; accentColor?: string }>(
  ({ theme, isReadOnly, accentColor }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0.75, 1),
    // Rounded like the afsprakenplanning appointment cards (8px), rather than the tighter cell radius.
    borderRadius: theme.spacing(1),
    border: '1px solid',
    borderColor: theme.palette.divider,
    // The left accent matches the category tile's symbol colour, so a card reads back to its tile.
    borderLeft: '3px solid',
    borderLeftColor: accentColor ?? theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    minWidth: 0,
    overflow: 'hidden',
    ...(isReadOnly && {
      fontStyle: 'italic',
      backgroundColor: theme.palette.grey[50],
      borderLeftColor: theme.palette.grey[400]
    }),
    // A ring drawn just outside the card (box-shadow, so it never nudges the layout) that wraps the whole
    // card, accent line included, and lifts it above its neighbours while selected.
    '&[data-selected="true"]': {
      boxShadow: `0 0 0 2px ${theme.palette.common.black}`,
      zIndex: 1
    }
  })
);

/**
 * The title line: what the appointment is, then when it is.
 *
 * A row rather than two lines because the card is only as wide as a day column. The activity takes
 * whatever room is left and truncates; the time is short and fixed, sits at the far end, and never
 * gives way.
 */
export const TitleRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'reserveMarkerSpace'
})<{ reserveMarkerSpace?: boolean }>(({ theme, reserveMarkerSpace }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(0.5),
  minWidth: 0,
  // The markers are drawn over this row's right end, so when there are any the row holds that much of
  // its width free — otherwise the time, which never shrinks, is what ends up under them. With no
  // markers nothing is reserved, so the time runs to the card edge rather than stopping short of a
  // column nothing is using.
  ...(reserveMarkerSpace && { paddingRight: SIGNAL_COLUMN_WIDTH })
}));

/**
 * The occurrence's start and end, pushed to the far end of the activity's line so the times line up
 * down a day column — the fastest way to spot two appointments that run into each other. It does not
 * shrink or wrap: a long activity truncates against it rather than pushing it off the card.
 *
 * Set in the supporting grey rather than the activity's black. There is whitespace between the two, so
 * the line still holds together with both weight and colour lightened; directly against the activity it
 * would not, which is why the earlier in-front layout was dropped.
 */
export const AppointmentTime = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isReadOnly'
})<{ isReadOnly?: boolean }>(({ theme, isReadOnly }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  fontWeight: 400,
  lineHeight: 1.3,
  marginLeft: 'auto',
  color: isReadOnly ? theme.palette.grey[600] : theme.palette.text.secondary
}));

// True black, since the theme's default text colour is a dark navy rather than black; a non-editable
// appointment fades to grey instead, matching afsprakenplanning's read-only treatment.
export const ActivityName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isReadOnly'
})<{ isReadOnly?: boolean }>(({ theme, isReadOnly }) => ({
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: 1.3,
  color: isReadOnly ? theme.palette.grey[700] : theme.palette.common.black,
  // Gives way to the time rather than pushing it out of the card: shrinks to whatever is left and
  // truncates there.
  flexShrink: 1,
  minWidth: 0
}));

/**
 * Participants read as plain text under the activity — never bold, so only the activity stands out.
 * Rendered only when there are participants; wraps onto a second line and then clamps with an ellipsis.
 *
 * Holds the markers' column free on the same terms as {@link TitleRow}, and off the same constant: two
 * markers stack down past the title line into this one, and this text wraps rather than truncating, so
 * it runs under them unless the room is kept. It used to reserve a hand-written `spacing(2)` — 16px
 * against a 22px column, which is exactly how far the text ran into the icons.
 */
export const Participant = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'reserveMarkerSpace'
})<{ reserveMarkerSpace?: boolean }>(({ reserveMarkerSpace }) => ({
  fontWeight: 400,
  lineHeight: 1.3,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  ...(reserveMarkerSpace && { paddingRight: SIGNAL_COLUMN_WIDTH })
}));

/**
 * Fills the card (its padding box) and acts as a size-query container for the markers, so the stack can
 * respond to how much room the card actually has. `pointer-events: none` keeps it clear of the card's
 * own hover and right-click. When the container is too short to stack the markers without crowding the
 * card, they collapse into a single count badge — the same overflow afsprakenplanning falls back to when
 * its icons don't fit. The threshold matches two 14px markers plus their gap and a little breathing room.
 * Any stack of two or more markers collapses (a lone marker always fits and keeps its icon), so a third
 * signal added later folds into the badge on its own without touching this rule.
 */
export const SignalOverlay = styled('span')({
  position: 'absolute',
  inset: 0,
  containerType: 'size',
  containerName: 'signals',
  pointerEvents: 'none',
  '@container signals (height < 38px)': {
    '& .signal-stack:not([data-signal-count="1"]) .signal-icon': {
      display: 'none'
    },
    '& .signal-stack:not([data-signal-count="1"]) .overflow-badge': {
      display: 'flex'
    }
  }
});

/**
 * The signal markers afsprakenplanning uses, anchored in the top-right corner and flowing downward, so
 * they read top-to-bottom in afsprakenplanning's order — the recurrence marker first, then the note
 * marker — the same corner afsprakenplanning stacks them in. Drawn in true black, the same black
 * afsprakenplanning draws its own markers in; `& path { fill: currentColor }` drives the exception SVG,
 * which otherwise ships with a hard-coded black fill.
 */
export const SignalStack = styled('span')(({ theme }) => ({
  position: 'absolute',
  right: SIGNAL_INSET,
  top: SIGNAL_INSET,
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.25),
  lineHeight: 0,
  color: theme.palette.common.black,
  '& svg': { width: SIGNAL_ICON_SIZE, height: SIGNAL_ICON_SIZE },
  '& path': { fill: 'currentColor' }
}));

/** One marker in the stack; carries the accessible label describing that signal. */
export const SignalIcon = styled('span')({
  display: 'inline-flex',
  lineHeight: 0
});

/**
 * The overflow fallback afsprakenplanning uses: an orange circle with the number of markers, shown only
 * (via the container query above) when the markers cannot fit. Hidden by default so it never doubles up
 * with the markers themselves. The colours are afsprakenplanning's own ORANGE/WHITE constants, so the
 * badge reads identically in both places. Its `font-style: normal` keeps it upright on a read-only
 * (italic) card.
 */
export const OverflowBadge = styled('span')(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  width: SIGNAL_ICON_SIZE,
  height: SIGNAL_ICON_SIZE,
  borderRadius: '50%',
  backgroundColor: ORANGE,
  color: WHITE,
  fontSize: 9,
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: 1,
  fontStyle: 'normal'
}));
